/**
 * The crouched walk and the crouch (ROUND7 §3): how a sneaking ranger looks. The sim hides a sneaker behind a
 * hedge (1.1 m) that hides no walker, so the drawing has to fit behind one too - at the rig's drawn scale his
 * helmet stays under about 1.3 m (tests/unit/look7Poses.test.ts). That is a deep crouch: hips down at shin
 * height behind his heels, chest folded forward over the knees, head up to watch the ground ahead, rifle held
 * low at his right side.
 *
 * The legs are solved (poseLegs.ts), not swung: each knee bends just far enough to put the sole on the ground
 * under the low pelvis, and the stride is measured off that solution so the planted foot does not slide. The
 * arms are solved to the rifle (poseArms.ts), which is placed in the body's frame, not the folded chest's.
 * Covering a flank or firing, he comes up enough to get the rifle into his shoulder, level.
 */

import { type BoneOf, type PoseInput } from './poseBase';
import { holdRifle, shoulderRifle } from './poseArms';
import { plantLeg } from './poseLegs';
import { B } from './rig';

/** Pelvis height in the crouch (standing: 0.88), and a little higher on the move, where the legs need room. */
export const CROUCH_PELVIS = 0.37;
const SNEAK_PELVIS = 0.4;
/** The hips sit back behind the feet, so the folded body is balanced over them. */
const HIPS_BACK = -0.12;
/** Chest folded forward over the knees... */
const LEAN = 1.33;
/** ...or raised enough to put the rifle in the shoulder. */
const LEAN_AIMING = 1.0;
/** Where the head looks, radians below the horizon: the ground a few metres ahead. */
const HEAD_DOWN = 0.75;
const HEAD_DOWN_AIMING = 0.1;
/** Mean forward swing of the thighs in the crouched walk, and how far each swings either side of it. */
const SNEAK_THIGH = 1.15;
const SNEAK_SWING = 0.4;
/** How high the stepping foot clears the ground. */
const SNEAK_LIFT = 0.08;
/**
 * Metres per full cycle (two steps). With the numbers above the planted sole travels back under him in each
 * half cycle by about this much again (tests/unit/look7Poses.test.ts measures the slide), so feet stay put.
 */
export const SNEAK_STRIDE = 0.8;

/** The same test `applyPose` uses to bring the rifle to the shoulder on the move. */
const aiming = (input: PoseInput): boolean => Math.abs(input.twist) > 0.6 || (input.kick ?? 0) > 0;

/**
 * Pelvis, chest and head of the crouch, and the rifle. The chest's rotation is yaw-then-lean ('YXZ') so the
 * twist `applyPose` adds toward the aim turns the folded body about the vertical, not about its own spine.
 */
function hunch(bone: BoneOf, pelvisY: number, up: boolean): void {
  const lean = up ? LEAN_AIMING : LEAN;
  bone(B.pelvis).position.set(0, pelvisY, HIPS_BACK);
  const spine = bone(B.spine).rotation;
  spine.order = 'YXZ';
  spine.x = lean;
  bone(B.head).rotation.x = (up ? HEAD_DOWN_AIMING : HEAD_DOWN) - lean;
  if (up) shoulderRifle(bone, lean);
  // Low and at his right side, clear of the knees, muzzle forward and down a little, angled across him.
  else holdRifle(bone, lean, [-0.24, -0.12, 0.34], 0.3, 0.3);
}

/** Standing still, crouched: one foot a little ahead of the other, breathing. `t` is the pose clock. */
export function crouch(bone: BoneOf, input: PoseInput, t: number): void {
  hunch(bone, CROUCH_PELVIS, aiming(input));
  bone(B.spine).rotation.x += Math.sin(t * 1.8) * 0.02;
  plantLeg(bone, B.lThigh, B.lShin, 1.35, CROUCH_PELVIS);
  plantLeg(bone, B.rThigh, B.rShin, 0.98, CROUCH_PELVIS);
}

/** Crouched behind the rifle: raised enough for the shoulder, the back knee almost down. Firing adds the kick. */
export function crouchAim(bone: BoneOf): void {
  hunch(bone, CROUCH_PELVIS, true);
  plantLeg(bone, B.lThigh, B.lShin, 1.4, CROUCH_PELVIS);
  plantLeg(bone, B.rThigh, B.rShin, 0.8, CROUCH_PELVIS);
}

/** The crouched walk: short quick steps from low hips, the stepping foot lifted clear. */
export function sneak(bone: BoneOf, input: PoseInput): void {
  const dir = input.reverse ? -1 : 1;
  const phase = (input.travelled / SNEAK_STRIDE) * Math.PI * 2 * dir;
  const sin = Math.sin(phase);
  const cos = Math.cos(phase);
  const pelvisY = SNEAK_PELVIS - 0.02 * Math.abs(sin);
  hunch(bone, pelvisY, aiming(input));
  bone(B.spine).rotation.y = 0.08 * sin;
  // The foot that is stepping - moving the way he travels, relative to his hips - is the one lifted:
  // forward on the forward walk, backward when he backs away (the phase runs the other way then).
  plantLeg(bone, B.lThigh, B.lShin, SNEAK_THIGH + SNEAK_SWING * sin, pelvisY, SNEAK_LIFT * Math.max(0, cos));
  plantLeg(bone, B.rThigh, B.rShin, SNEAK_THIGH - SNEAK_SWING * sin, pelvisY, SNEAK_LIFT * Math.max(0, -cos));
}
